import React from "react";
import { Document, Page, Path, Rect, Svg, Text, View, pdf } from "@react-pdf/renderer";
import QRCode from "qrcode";
import { labelTypography, mmToPt, type PlacedBatchLabel } from "./processedBatchLabels";

function qrVector(url: string) {
  const modules = QRCode.create(url, { errorCorrectionLevel: "M" }).modules;
  let path = "";
  for (let row = 0; row < modules.size; row++) for (let column = 0; column < modules.size; column++) {
    if (!modules.data[row * modules.size + column]) continue;
    const start = column;
    while (column + 1 < modules.size && modules.data[row * modules.size + column + 1]) column++;
    const length = column - start + 1;
    path += `M${start + 4} ${row + 4}h${length}v1h-${length}z`;
  }
  return { path, size: modules.size + 8 };
}

export function ProcessedBatchLabelsPdf({ pages }: { pages: PlacedBatchLabel[][] }) {
  const codes = new Map(pages.flat().map(({ label }) => [label.url, qrVector(label.url)]));
  return <Document title="VERT.farm · Batch-etiketter" author="VERT.farm">
    {pages.map((page, pageIndex) => <Page key={pageIndex} size="A4" orientation="portrait" style={{ padding: 0, backgroundColor: "#ffffff" }}>
      {page.map(({ label, x, y, width, height, rotated }, index) => {
        const { settings } = label;
        const { fontSize, lines, qrMm } = labelTypography(label);
        const code = codes.get(label.url)!;
        if (qrMm / code.size < 0.25) throw new Error("QR-koden kräver en högre etikett för denna produktlänk. Öka etikettens höjd.");
        return <View key={index} wrap={false} style={{ position: "absolute", left: mmToPt(x), top: mmToPt(y), width: mmToPt(width), height: mmToPt(height) }}>
          <View style={{ position: "absolute", top: 0, left: rotated ? mmToPt(settings.heightMm) : 0,
            width: mmToPt(settings.widthMm), height: mmToPt(settings.heightMm), backgroundColor: settings.backgroundColor,
            ...(rotated ? { transform: "rotate(90deg)", transformOrigin: "0% 0%" } : {}) }}>
            <Text style={{ position: "absolute", left: mmToPt(2), top: mmToPt(3.2), width: mmToPt(settings.widthMm - qrMm - 6) + 0.2,
              fontFamily: "Courier", fontSize, lineHeight: 1.25, color: settings.textColor }}>{lines.join("\n")}</Text>
            {/* The wider frame replaces outer white padding; QR size and quiet zone stay intact. */}
            <View style={{ position: "absolute", right: mmToPt(1.5), top: mmToPt(2.7), width: mmToPt(qrMm + 1), height: mmToPt(qrMm + 1),
              borderWidth: mmToPt(0.5), borderColor: settings.accentColor, backgroundColor: "#ffffff" }} />
            <Svg style={{ position: "absolute", right: mmToPt(2), top: mmToPt(3.2), width: mmToPt(qrMm), height: mmToPt(qrMm) }}
              viewBox={`0 0 ${code.size} ${code.size}`}>
              <Rect x={0} y={0} width={code.size} height={code.size} fill="#ffffff" />
              <Path d={code.path} fill="#000000" />
            </Svg>
          </View>
        </View>;
      })}
    </Page>)}
  </Document>;
}

export async function createProcessedBatchLabelsPdf(pages: PlacedBatchLabel[][]): Promise<Blob> {
  return pdf(<ProcessedBatchLabelsPdf pages={pages} />).toBlob();
}
